import React, { ReactNode, ReactElement } from 'react';
import ToolTip from '../ToolTip';

import './Label.css';

interface ILabelProps {
	/** To apply styles to Label component. */
	className?: string;
	/** To apply styles to Label component text element. */
	textClass?: string;
	/** Text for the label. */
	children: ReactNode;
	/** Short descriptions added in parentheses next to the Label that provide help for the user. */
	helperText?: string | ReactElement | ReactElement[];
	/** Tooltip content. Tooltip icon appears only when the content is not empty. */
	tooltip?: string | ReactElement | ReactElement[];
	/** Alignment of tooltip in desktop view. */
	tooltipAlign?:
		| 'bottom-center'
		| 'bottom-left'
		| 'bottom-right'
		| 'top-center'
		| 'top-left'
		| 'top-right';
	/** Alignment of tooltip in mobile view. */
	mobileTooltipAlign?:
		| 'bottom-center'
		| 'bottom-left'
		| 'bottom-right'
		| 'top-center'
		| 'top-left'
		| 'top-right'
		| null;
	/** Denotes whether the associated input component needs to be filled out by the user. */
	required?: boolean;
	/** It will receive the error state */
	error?: boolean;
	alternate?: boolean;
}

/**
 * A segment of text that is meant to be displayed above an input component. It helps describe what data the input is requesting.
 */
const Label = ({
	className = '',
	children,
	helperText,
	textClass = '',
	required = false,
	tooltip,
	error = false,
	tooltipAlign = 'bottom-left',
	mobileTooltipAlign = 'bottom-right',
	alternate = false,
}: ILabelProps): JSX.Element => {

	return (
		<div className={`Label flex justify-between ${className}`} tabIndex={-1}>
			<div className='Label__text_wrapper inline-flex items-center flex-row'>
				<span
					className={`Label__text ${textClass} ${
						error ? 'Label__text__error' : 'text-grey-mine-shaft'
					}`}
				>
					{children}
					{required && !helperText && <sup className='Label__text__required'>*</sup>}
				</span>
				{helperText && (
					<span
						className={`flex items-center mr-0 ml-2 Label__text__helper_text${
							error && alternate ? '--error' : ' text-grey-tundora'
						}`}
					>
						({helperText})
						{required &&  <sup className='Label__text__required'>*</sup>}
					</span>
				)}
				{tooltip && (
					<ToolTip
						className={`Label__tooltip ${
							!required ? 'addMoreMargin' : ''
						}`}
						notchPosition={tooltipAlign}
						mobileNotchPosition={mobileTooltipAlign || tooltipAlign}
						alternate={alternate}
					>
						{tooltip}
					</ToolTip>
				)}
			</div>
		</div>
	);
};

export default Label;
